import React, { useMemo } from 'react';
import { useMaybeSimpleHousehold } from 'Hooks/useSimpleHousehold';
import { useMaybeUserContext } from 'Context/UserContext';
import { getUICommitHash } from 'EventTracking/utils';

import categoriesImport from '../consts/categories';
import eventTypesImport from '../consts/eventTypes';
import eventSubTypesImport from '../consts/eventSubTypes';

import logEventActions from './logEventActions';
import constructStandardEventProperties from './standardEventProperties';
import getDOMBreadcrumbs from './DOMBreadcrumbs';
import getURLBreadcrumbs from './URLBreadcrumbs';

const SEPARATOR = '>';

export const eventTypes = eventTypesImport;
export const eventSubTypes = eventSubTypesImport;
export const categories = categoriesImport;

const assertValidStringArg = (arg, argName) => {
  if (typeof arg !== 'string') {
    throw new Error(`Developer Configuration Error: ${argName} must be of type string`);
  }

  if (arg.length === 0) {
    throw new Error(`Developer Configuration Error: ${argName} must have at least 1 character`);
  }

  if (arg.includes(SEPARATOR)) {
    throw new Error(`Developer Configuration Error: ${argName} may not include special character '${SEPARATOR}'`);
  }
};

const assertValidCategory = (category) => {
  assertValidStringArg(category, 'category');

  if (!Object.values(categories).find((v) => (v === category))) {
    throw new Error(`Developer Configuration Error: Category "${category}" not found in map of categories. Please add to categories.js`);
  }
};

const assertValidEventType = (eventType) => {
  assertValidStringArg(eventType, 'eventType');

  if (!Object.values(eventTypes).find((v) => (v === eventType))) {
    throw new Error(`Developer Configuration Error: Event Type "${eventType}" not found in map of types. Please add to eventTypes.js`);
  }
};

const assertValidEventSubType = (eventSubType) => {
  assertValidStringArg(eventSubType, 'eventSubType');
};

const buildEventName = (components) => {
  return components.filter(Boolean).join(` ${SEPARATOR} `).toLowerCase();
};

const logStandardizedEventWrapper = (logEventFn, ref, additionalProperties) => (category, eventType, eventSubType = '', args = {}) => {
  const {
    eventProperties = {},
  } = args;

  assertValidCategory(category);
  assertValidEventType(eventType);
  assertValidEventSubType(eventSubType);

  const eventName = buildEventName([category, eventType, eventSubType]);

  return logEventFn(eventName, {
    domBreadcrumbs: getDOMBreadcrumbs(eventName, ref),
    urlBreadcrumbs: getURLBreadcrumbs(eventName),
    uiCommitHash: getUICommitHash(),
    ...additionalProperties,
    ...eventProperties,
  });
};

const logStandardizedEventFailureWithErrorWrapper = (logEventFn, ref, additionalProperties) => (category, eventType, eventSubType, e, args = {}) => {
  const {
    eventProperties = {},
  } = args;

  assertValidCategory(category);
  assertValidEventType(eventType);
  assertValidEventSubType(eventType);

  const eventName = buildEventName([category, eventType, eventSubType]);

  return logEventFn(eventName, e, {
    domBreadcrumbs: getDOMBreadcrumbs(eventName, ref),
    urlBreadcrumbs: getURLBreadcrumbs(eventName),
    uiCommitHash: getUICommitHash(),
    ...additionalProperties,
    ...eventProperties,
  });
};

const logEventWrapper = (logEventFn, ref, additionalProperties) => (eventName, eventProperties = null) => {
  return logEventFn(eventName, {
    domBreadcrumbs: getDOMBreadcrumbs(eventName, ref),
    urlBreadcrumbs: getURLBreadcrumbs(eventName),
    uiCommitHash: getUICommitHash(),
    ...additionalProperties,
    ...eventProperties,
  });
};

const logEventFailureWithErrorWrapper = (logEventFn, ref, additionalProperties) => (eventName, error, eventProperties) => {
  return logEventFn(eventName, error, {
    domBreadcrumbs: getDOMBreadcrumbs(eventName, ref),
    urlBreadcrumbs: getURLBreadcrumbs(eventName),
    uiCommitHash: getUICommitHash(),
    ...additionalProperties,
    ...eventProperties,
  });
};

const logResourceEventWrapper = (logEventFn, ref, additionalProperties) => (eventName, resource, eventProperties) => {
  return logEventFn(eventName, resource, {
    domBreadcrumbs: getDOMBreadcrumbs(eventName, ref),
    urlBreadcrumbs: getURLBreadcrumbs(eventName),
    uiCommitHash: getUICommitHash(),
    ...additionalProperties,
    ...eventProperties,
  });
};

const logResourceEventFailureWithErrorWrapper = (logEventFn, ref, additionalProperties) => (eventName, resource, error, eventProperties) => {
  return logEventFn(eventName, resource, error, {
    uiCommitHash: getUICommitHash(),
    ...additionalProperties,
    ...eventProperties,
  });
};

export default function useEventTrackingLogEvent(args = {}) {
  const {
    ref: externalRef,
    nodeName,
    isPageView,
    pageViewEventProperties,
  } = args;

  const internalRef = React.useRef();

  React.useEffect(() => {
    if (isPageView) {
      logEventActions.logPageView(nodeName, pageViewEventProperties);
    }
  }, []);

  const householdResource = useMaybeSimpleHousehold()?.householdResource;
  const isAgent = useMaybeUserContext()?.isAgent;
  const eventProperties = useMemo(() => constructStandardEventProperties({
    isAgent,
    householdResource,
  }), [isAgent, householdResource]);

  // Give preference to a passed in ref
  const ref = externalRef || internalRef;

  const rootNodeProps = { ref };
  if (nodeName) {
    rootNodeProps['data-eventtrackingnodename'] = nodeName;
  }

  return {
    rootNodeProps,
    // This will log events in a standardized form.
    logStandardizedEvent: useMemo(() => logStandardizedEventWrapper(logEventActions.logEvent, ref, eventProperties), [ref, eventProperties]),
    // This is what you'll use most of the time. It's the basic version. This is the goto action.
    logEvent: useMemo(() => logEventWrapper(logEventActions.logEvent, ref, eventProperties), [ref, eventProperties]),
    // Same as logEvent but appends '[SUCCESS]' to the eventName on logEvent.
    logEventSuccess: useMemo(() => logEventWrapper(logEventActions.logEventSuccess, ref, eventProperties), [ref, eventProperties]),
    // Same as logEvent but appends '[FAILED]' to the eventName on logEvent.
    logEventFailure: useMemo(() => logEventWrapper(logEventActions.logEventFailure, ref, eventProperties), [ref, eventProperties]),
    // Same as logEventFailure but adds error information onto the event (IE. traceId)
    logEventFailureWithError: useMemo(() => logEventFailureWithErrorWrapper(logEventActions.logEventFailureWithError, ref, eventProperties), [ref, eventProperties]),
    // Same as logEventFailureWithError but builds the event name like logStandardizedEvent
    logStandardizedEventFailureWithError: useMemo(() => logStandardizedEventFailureWithErrorWrapper(logEventActions.logEventFailureWithError, ref, eventProperties), [ref, eventProperties]),
    // Same as logEvent but adds information about a resource into the event properties
    logResourceEvent: useMemo(() => logResourceEventWrapper(logEventActions.logResourceEvent, ref, eventProperties), [ref, eventProperties]),
    // Same as logEventSuccess but adds information about a resource into the event properties
    logResourceEventSuccess: useMemo(() => logResourceEventWrapper(logEventActions.logResourceEventSuccess, ref, eventProperties), [ref, eventProperties]),
    // Same as logEventFailure but adds information about a resource into the event properties
    logResourceEventFailure: useMemo(() => logResourceEventWrapper(logEventActions.logResourceEventFailure, ref, eventProperties), [ref, eventProperties]),
    // Same as logEventFailure but adds information about a resource and error details into the event properties
    logResourceEventFailureWithError: useMemo(() => logResourceEventFailureWithErrorWrapper(logEventActions.logResourceEventFailureWithError, ref, eventProperties), [ref, eventProperties]),
  };
}
